import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { setLogin, setLogout } from '../state/index'
import { API_URL, LICENSE_KEY } from 'state/constants';


const baseQuery = fetchBaseQuery({
    baseUrl: API_URL,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
        if (LICENSE_KEY) {
            headers.set('x-license-key', LICENSE_KEY)
        }
        const token = getState().token
        if (token) {
            headers.set('Authorization', `Bearer ${token}`)
        }
        return headers
    }
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions)

    if (result?.error?.originalStatus === 403) {
        const refreshResult = await baseQuery('/auth/refresh', api, extraOptions)
        
        if (refreshResult?.data) {
            const user = api.getState().user
            // store the new token 
            api.dispatch(setLogin({ ...refreshResult.data, user }))
            // retry the original query with new access token 
            result = await baseQuery(args, api, extraOptions)
        } else {
            api.dispatch(setLogout())
        }
    }

    return result
}

export const apiSlice = createApi({
    baseQuery: baseQueryWithReauth,
    endpoints: builder => ({})
})

export const homepageConfigApi = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getHomepageConfig: builder.query({ query: () => '/homepageconfig' }),
        updateHomepageConfig: builder.mutation({
            query: (body) => ({ url: '/homepageconfig', method: 'PUT', body }),
        }),
        resetHomepageConfig: builder.mutation({
            query: () => ({ url: '/homepageconfig/reset', method: 'POST' }),
        }),
    }),
    overrideExisting: false,
})

export const {
    useGetHomepageConfigQuery,
    useUpdateHomepageConfigMutation,
    useResetHomepageConfigMutation,
} = homepageConfigApi

export const aiApi = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getAIHealth: builder.query({ query: () => '/ai/health' }),
        sendAIChat: builder.mutation({
            query: (body) => ({ url: '/ai/chat', method: 'POST', body }),
        }),
        createAIConversation: builder.mutation({
            query: () => ({ url: '/conversations/ai', method: 'POST' }),
        }),
        analyzeData: builder.mutation({
            query: (body) => ({ url: '/ai/analyze', method: 'POST', body }),
        }),
        draftContent: builder.mutation({
            query: (body) => ({ url: '/ai/draft', method: 'POST', body }),
        }),
        smartSearch: builder.mutation({
            query: (body) => ({ url: '/ai/search', method: 'POST', body }),
        }),
    }),
    overrideExisting: false,
})

export const {
    useGetAIHealthQuery,
    useSendAIChatMutation,
    useCreateAIConversationMutation,
    useAnalyzeDataMutation,
    useDraftContentMutation,
    useSmartSearchMutation,
} = aiApi

export const scheduleSeasonsApi = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getSeasons: builder.query({
            query: (params = {}) => ({ url: '/schedule-seasons', params }),
        }),
        getSeasonById: builder.query({
            query: (id) => `/schedule-seasons/${id}`,
        }),
        getSeasonStats: builder.query({
            query: (id) => `/schedule-seasons/${id}/stats`,
        }),
        createSeason: builder.mutation({
            query: (body) => ({ url: '/schedule-seasons/new', method: 'POST', body }),
        }),
        updateSeason: builder.mutation({
            query: ({ id, ...body }) => ({ url: `/schedule-seasons/update/${id}`, method: 'PUT', body }),
        }),
        duplicateSeason: builder.mutation({
            query: ({ id, ...body }) => ({ url: `/schedule-seasons/${id}/duplicate`, method: 'POST', body }),
        }),
        deleteSeasons: builder.mutation({
            query: (ids) => ({ url: '/schedule-seasons/delete', method: 'DELETE', body: { ids } }),
        }),
    }),
    overrideExisting: false,
})

export const {
    useGetSeasonsQuery,
    useGetSeasonByIdQuery,
    useGetSeasonStatsQuery,
    useCreateSeasonMutation,
    useUpdateSeasonMutation,
    useDuplicateSeasonMutation,
    useDeleteSeasonsMutation,
} = scheduleSeasonsApi

export const scheduledFlightsApi = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getScheduledFlights: builder.query({
            query: (params = {}) => ({ url: '/scheduled-flights', params }),
        }),
        getScheduledFlightById: builder.query({
            query: (id) => `/scheduled-flights/${id}`,
        }),
        getConflicts: builder.query({
            query: (seasonId) => ({ url: '/scheduled-flights/conflicts', params: { seasonId } }),
        }),
        createScheduledFlight: builder.mutation({
            query: (body) => ({ url: '/scheduled-flights/new', method: 'POST', body }),
        }),
        updateScheduledFlight: builder.mutation({
            query: ({ id, ...body }) => ({ url: `/scheduled-flights/update/${id}`, method: 'PUT', body }),
        }),
        deleteScheduledFlights: builder.mutation({
            query: (ids) => ({ url: '/scheduled-flights/delete', method: 'DELETE', body: { ids } }),
        }),
        publishSchedule: builder.mutation({
            query: (body) => ({ url: '/scheduled-flights/publish', method: 'POST', body }),
        }),
    }),
    overrideExisting: false,
})

export const {
    useGetScheduledFlightsQuery,
    useGetScheduledFlightByIdQuery,
    useGetConflictsQuery,
    useCreateScheduledFlightMutation,
    useUpdateScheduledFlightMutation,
    useDeleteScheduledFlightsMutation,
    usePublishScheduleMutation,
} = scheduledFlightsApi

export const aircraftRotationsApi = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getAircraftRotations: builder.query({
            query: (params = {}) => ({ url: '/aircraft-rotations', params }),
        }),
        getAircraftRotationById: builder.query({
            query: (id) => `/aircraft-rotations/${id}`,
        }),
        getFleetUtilization: builder.query({
            query: (params = {}) => ({ url: '/aircraft-rotations/fleet-utilization', params }),
        }),
        createAircraftRotation: builder.mutation({
            query: (body) => ({ url: '/aircraft-rotations/new', method: 'POST', body }),
        }),
        updateAircraftRotation: builder.mutation({
            query: ({ id, ...body }) => ({ url: `/aircraft-rotations/update/${id}`, method: 'PUT', body }),
        }),
        deleteAircraftRotations: builder.mutation({
            query: (ids) => ({ url: '/aircraft-rotations/delete', method: 'DELETE', body: { ids } }),
        }),
        validateRotation: builder.mutation({
            query: ({ id, ...body }) => ({ url: `/aircraft-rotations/${id}/validate`, method: 'POST', body }),
        }),
        addRotationLeg: builder.mutation({
            query: ({ id, ...body }) => ({ url: `/aircraft-rotations/${id}/legs`, method: 'POST', body }),
        }),
        removeRotationLeg: builder.mutation({
            query: ({ id, legIndex }) => ({ url: `/aircraft-rotations/${id}/legs/${legIndex}`, method: 'DELETE' }),
        }),
    }),
    overrideExisting: false,
})

export const {
    useGetAircraftRotationsQuery,
    useGetAircraftRotationByIdQuery,
    useGetFleetUtilizationQuery,
    useCreateAircraftRotationMutation,
    useUpdateAircraftRotationMutation,
    useDeleteAircraftRotationsMutation,
    useValidateRotationMutation,
    useAddRotationLegMutation,
    useRemoveRotationLegMutation,
} = aircraftRotationsApi

export const referenceApi = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getActiveAcTypes: builder.query({
            query: () => '/actypes/active',
        }),
        getActiveAircrafts: builder.query({
            query: () => '/aircrafts/active',
        }),
        getAirports: builder.query({
            query: () => '/airports',
        }),
    }),
})

export const {
    useGetActiveAcTypesQuery,
    useGetActiveAircraftsQuery,
    useGetAirportsQuery,
} = referenceApi


